import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminStore } from '../../../../stores';
import useProductFormValidationSchema from './useProductFormValidationSchema';
import {
	convertUrlsToFiles,
	handleImageUpload,
	refreshQuery,
	removeEmptyKeys,
} from '../../../../utils/utils';
import { useFormik } from 'formik';
import { MimaToastUtil } from '../../../../components';
import constant from '../../../../utils/constant';
import { AdminController } from '../../../../controllers';

export const useAdminEditProductsLogic = () => {
	const navigate = useNavigate();
	const { businessId, selectedProduct } = adminStore;
	const { validationSchema } = useProductFormValidationSchema();

	const [mappedVariations, setMappedVariations] = useState([]);
	const [blobbedImages, setBlobbedImages] = useState([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		let isMounted = true;

		const hydrateFormData = async () => {
			setIsLoading(true);

			try {
				const variations = selectedProduct?.variations || [];
				const mapped = await Promise.all(
					variations.map(
						async ({
							colorsAvailable,
							sellingPrices,
							_id,
							documentUrls,
							...rest
						}) => {
							const primaryColors = (colorsAvailable || [])
								.filter(x => x.type === 'PRIMARY')
								.map(({ _id, ...noIdColor }) => ({ ...noIdColor }));

							const customColors = (colorsAvailable || [])
								.filter(x => x.type === 'CUSTOM')
								.map(({ color }) => color);

							const priceList = sellingPrices || [];
							const local =
								priceList
									.filter(x => x.type === 'LOCAL')
									.map(({ _id, type, ...noIdLocal }) => ({
										...noIdLocal,
									}))[0] || {};

							const international =
								priceList
									.filter(x => x.type === 'INTERNATIONAL')
									.map(
										({
											_id,
											stripeWithMaterialId,
											withoutMaterial,
											type,
											...noIdInt
										}) => ({
											...noIdInt,
										})
									)[0] || {};

							let docs = [];
							if (documentUrls?.length > 0) {
								try {
									docs = await convertUrlsToFiles(
										documentUrls.map((url, i) => ({
											url,
											fileName: `doc-${i}`,
										}))
									);
								} catch (error) {
									console.error('Error converting URLs to files', error);
								}
							}

							return {
								...rest,
								colorsAvailable: primaryColors,
								customColors,
								addCustomColor: customColors.length > 0,
								local,
								international,
								documentUrls: docs,
							};
						}
					)
				);

				let images = [];
				const imageUrls = selectedProduct?.imageUrls;
				if (imageUrls?.length > 0) {
					try {
						images = await convertUrlsToFiles(
							imageUrls.map((url, i) => ({
								url,
								fileName: `Image-${i}`,
							}))
						);
					} catch (error) {
						console.error('Error converting URLs to files', error);
					}
				}

				if (isMounted) {
					setMappedVariations(mapped);
					setBlobbedImages(images);
				}
			} finally {
				if (isMounted) {
					setIsLoading(false);
				}
			}
		};

		hydrateFormData();

		return () => {
			isMounted = false;
		};
	}, [selectedProduct]);

	const initialValues = useMemo(
		() => ({
			name: selectedProduct?.name || '',
			pricingModel: selectedProduct?.pricingModel || 'FIXED_PRICE',
			imageUrls: blobbedImages || [],
			topView: selectedProduct?.topView || false,
			createCollections: false,
			styleCollections: selectedProduct?.styleCollections || [],
			newCollections: [],
			daysRequiredForDelivery: selectedProduct?.daysRequiredForDelivery || {
				early: 1,
				late: 1,
			},
			type: selectedProduct?.type || 'PRODUCT',
			availableWindow: selectedProduct?.availableWindow || {
				startHour: 0,
				endHour: 0,
			},
			availableDays: selectedProduct?.availableDays || [],
			bestSelling: selectedProduct?.bestSelling || false,
			variations: mappedVariations,
			hasVariations: (selectedProduct?.variations || []).length > 1,
			description: selectedProduct?.description || '',
			gender: selectedProduct?.gender || '',
			sellAbroad: Boolean(mappedVariations[0]?.international?.currencyCode),
		}),
		[selectedProduct, mappedVariations, blobbedImages]
	);

	const onSubmit = async values => {
		if (values.imageUrls?.length < 1) {
			return MimaToastUtil.error({
				message: 'Kindly Add atleast 1 image ',
			});
		}

		let payload;

		const imagesToUpload = values.imageUrls?.filter(x => !x.originalUrl);
		const oldImageUrls = values.imageUrls
			?.filter(x => x.originalUrl)
			.map(z => z.originalUrl);

		let newImageUrls = [];

		if (imagesToUpload.length > 0) {
			newImageUrls = await handleImageUpload(imagesToUpload);
			if (newImageUrls.length < 1 || newImageUrls === constant.Failed) {
				return MimaToastUtil.error({
					message: 'Image upload failed, try again',
				});
			}
		}

		const urls = [...oldImageUrls, ...newImageUrls];

		const unifiedCollections = [
			...values.styleCollections,
			...values.newCollections,
		];

		const updatedVariations = await Promise.all(
			values.variations.map(
				async ({
					customColors,
					colorsAvailable,
					international,
					local,
					addCustomColor,
					isDigital,
					documentUrls,
					...rest
				}) => {
					const customColorsObjects = customColors?.map(color => ({
						color,
						type: 'CUSTOM',
					}));

					let urls = [];

					if (documentUrls?.length > 0) {
						urls = await handleImageUpload(documentUrls);
					}

					return {
						...rest,
						...(values.sellAbroad && values.pricingModel !== 'BESPOKE'
							? { international }
							: {}),
						...(values.pricingModel !== 'BESPOKE' ? { local } : {}),
						...(colorsAvailable?.length > 0 || customColorsObjects?.length > 0
							? {
									colorsAvailable: [
										...(colorsAvailable || []),
										...(customColorsObjects || []),
									].filter(item => item && Object.keys(item).length > 0),
								}
							: {}),
						isDigital,
						documentUrls: urls,
					};
				}
			)
		);

		const prePayload = {
			...values,
			variations: updatedVariations,
		};

		let typeUpdatedVariations;

		if (values.type === 'PRODUCT') {
			delete prePayload.availableDays;
			delete prePayload.availableWindow;

			typeUpdatedVariations = prePayload.variations.map(
				({ durationInMinutes, ...rest }) => rest
			);
		}

		if (values.type === 'SERVICE') {
			typeUpdatedVariations = [...prePayload.variations];
		}

		payload = {
			...prePayload,
			variations: typeUpdatedVariations,
			imageUrls: urls,
			styleCollections: unifiedCollections,
		};

		delete payload.createCollections;
		delete payload.hasVariations;
		delete payload.newCollections;
		delete payload.sellAbroad;

		if (!values.description) {
			delete payload.description;
		}

		removeEmptyKeys(payload);

		const { status, errorMessage } = await AdminController.editProduct(
			payload,
			selectedProduct._id
		);

		if (status === constant.Success) {
			const id = businessId;
			refreshQuery({ queryKey: [id, constant.apiQueryKey.getProducts] });
			MimaToastUtil.success({
				message: constant.Success,
			});
			return navigate(constant.Routes.Admin.Products);
		}

		return MimaToastUtil.error({
			message: errorMessage,
		});
	};

	const formik = useFormik({
		initialValues,
		enableReinitialize: true,
		validationSchema,
		onSubmit,
	});

	return {
		formik,
		isLoading,
	};
};

export default useAdminEditProductsLogic;
